/**
 * One clinic, seen from the platform side: registration, plan, stores, people
 * and usage history.
 *
 * Everything on this page is metadata, which is why a JMC operator sees it
 * without elevating. The clinic's article content is the one thing absent, and
 * stays absent — reading it needs a time-limited, reason-bound, audited
 * support elevation.
 */
import { ThemePicker } from '../components/ThemePicker.tsx';
import { ROLE_LABEL } from '../roles.ts';
import { useState } from 'react';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import type { AdminClinicDetail, AdminPlan, ListResponse } from '../api.ts';
import { del, patch, post } from '../api.ts';
import { DangerButton } from '../components/DangerButton.tsx';
import {
  ClinicPromptEditor,
  type ClinicPromptStyleData,
} from '../components/ClinicPromptEditor.tsx';
import { ClinicAllowedHosts, type AllowedHost } from '../components/ClinicAllowedHosts.tsx';
import { ClinicFacts, type ClinicFactsData } from '../components/ClinicFacts.tsx';
import {
  Card,
  Empty,
  Pill,
  RowLink,
  Stat,
  StatusPill,
  Table,
  jaDate,
  jaNumber,
  limitText,
} from '../components/ui.tsx';
import { SkeletonTiles, usePendingScreen } from '../components/Skeleton.tsx';
import { EditPanel, Field } from '../components/EditPanel.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { DateInput } from '../components/DateInput.tsx';
import {
  ClinicProfileFields,
  StoreProfileFields,
  clinicProfileFrom,
  clinicProfilePayload,
  storeProfilePayload,
  type StoreProfileForm,
} from '../components/ProfileFields.tsx';
import { AddMember } from '../components/AddMember.tsx';
import { MemberEditor } from '../components/MemberEditor.tsx';
import { ModalForm } from '../components/Modal.tsx';
import { Select } from '../components/Select.tsx';

/**
 * One registered value.
 *
 * A dash rather than an empty cell for anything unset: an empty cell reads as
 * a rendering fault, and 「未登録」 on ten rows at once reads as an error list.
 */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="factgrid__row">
      <dt>{label}</dt>
      <dd>{children || <span className="muted">—</span>}</dd>
    </div>
  );
}

/** Whole days until the contract ends. null when it is open-ended. */
const daysUntil = (iso: string | null): number | null =>
  iso === null ? null : Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);

/** A store as the add dialog holds it: a name, the details, one override. */
interface NewStore extends StoreProfileForm {
  name: string;
  wordpressCategory: string;
}

const emptyStore = (): NewStore => ({
  name: '',
  postalCode: '',
  prefecture: '',
  city: '',
  address: '',
  accessNote: '',
  phone: '',
  hoursNote: '',
  closedNote: '',
  reservationUrl: '',
  mapUrl: '',
  wordpressCategory: '',
});

const monthLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short' });

interface LoaderData extends AdminClinicDetail {
  /** JMC's wording for this clinic, and the requests that argued for it. */
  promptStyles: ClinicPromptStyleData;
  /** The clinic's registered material, which JMC may set up and approve. */
  facts: ClinicFactsData;
  /** The domains this clinic's AI may read, beyond its own site URL. */
  allowedHosts: { items: AllowedHost[] };
  plans: ListResponse<AdminPlan>;
}

/** Seeds the edit form from the saved record. */
const formFrom = (clinic: AdminClinicDetail['clinic']) => ({
  name: clinic.name,
  status: clinic.status,
  planId: clinic.plan_id ?? '',
  subscriptionStatus: clinic.subscription_status ?? 'ACTIVE',
  // <input type="date"> speaks YYYY-MM-DD; the column is a timestamp.
  endsAt: clinic.ends_at ? clinic.ends_at.slice(0, 10) : '',
  periodEnd: clinic.current_period_end ? clinic.current_period_end.slice(0, 10) : '',
  siteName: clinic.site_name ?? '',
  siteUrl: clinic.site_url ?? '',
  defaultTone: clinic.default_tone ?? '',
  wordpressCategory: clinic.wordpress_category ?? '',
  // NOT NULL on the column, so an absent list is empty rather than inherited -
  // a clinic is the top of the chain and has nothing to inherit from.
  defaultKeywords: clinic.default_keywords ?? [],
});

export default function AdminClinicDetailPage() {
  const navigate = useNavigate();
  const {
    clinic,
    stores,
    members,
    usage,
    reconciliation,
    activity,
    plans,
    promptStyles,
    facts,
    allowedHosts,
  } = useLoaderData<LoaderData>();
  const revalidator = useRevalidator();
  const pending = usePendingScreen();

  const [editing, setEditing] = useState(false);
  const [addingStore, setAddingStore] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [form, setForm] = useState(() => formFrom(clinic));
  // The operating entity, which is not the same record as its article defaults.
  const [profile, setProfile] = useState(() => clinicProfileFrom(clinic));
  const [newStore, setNewStore] = useState<NewStore>(emptyStore());

  /*
   * What the plan allows, and whether this clinic is there.
   *
   * The number comes from the plan the clinic is actually on, so the dialog can
   * say so BEFORE the operator types a name and is refused by the server. The
   * server still refuses - a screen is not an authorization - but being told
   * afterwards is what made this feel arbitrary.
   */
  const storeLimit =
    clinic.plan_limits.stores === undefined ? null : Number(clinic.plan_limits.stores);
  const atStoreLimit = storeLimit !== null && stores.length >= storeLimit;

  const saveClinic = () =>
    patch(`/admin/clinics/${clinic.id}`, {
      name: form.name,
      status: form.status,
      ...(form.planId ? { planId: form.planId } : {}),
      // Only when it actually moves: sending the current value would write a row
      // and an audit entry for an edit to the clinic's name.
      ...(form.subscriptionStatus !== clinic.subscription_status
        ? { subscriptionStatus: form.subscriptionStatus }
        : {}),
      // Only when it moves. '' is a term cleared, which is a real instruction
      // (open-ended) and not the same as leaving the field alone.
      ...(form.endsAt !== (clinic.ends_at ? clinic.ends_at.slice(0, 10) : '')
        ? { endsAt: form.endsAt || null }
        : {}),
      // Only when moved, and never cleared: a subscription always has a period.
      ...(form.periodEnd &&
      form.periodEnd !== (clinic.current_period_end ? clinic.current_period_end.slice(0, 10) : '')
        ? { periodEnd: form.periodEnd }
        : {}),
      siteName: form.siteName,
      // An emptied URL field clears the value rather than storing "".
      siteUrl: form.siteUrl ? form.siteUrl : null,
      defaultTone: form.defaultTone,
      wordpressCategory: form.wordpressCategory,
      defaultKeywords: form.defaultKeywords,
      ...clinicProfilePayload(profile),
    });

  const termDays = daysUntil(clinic.ends_at);
  const [renewMonths, setRenewMonths] = useState('12');
  const [renewing, setRenewing] = useState(false);

  async function renew() {
    setRenewing(true);
    try {
      await post(`/admin/clinics/${clinic.id}/renew`, { months: Number(renewMonths) });
      await revalidator.revalidate();
    } finally {
      setRenewing(false);
    }
  }

  const totals = usage.reduce(
    (acc, row) => ({
      articles: acc.articles + Number(row.articles_used),
      images: acc.images + Number(row.images_used),
      cost: acc.cost + Number(row.ai_micro_cost_used),
    }),
    { articles: 0, images: 0, cost: 0 },
  );

  return (
    <>
      {/*
        Said at the top, in the clinic's own words, because it explains every
        refusal its staff are currently seeing: generation stops, everything
        else keeps working.
      */}
      {termDays !== null && termDays < 0 && (
        <p className="alert" role="status">
          ご契約期間は{jaDate(clinic.ends_at)}に終了しています。
          AI機能（記事・画像の生成、サイトからの読み取り）は停止中です。
          ログイン・既存記事の閲覧・編集・書き出しは通常どおり行えます。
        </p>
      )}

      <Card
        title={clinic.name}
        actions={
          <>
            {/*
              Renewal is one press and a number of months, because that is the
              whole transaction. It extends from whichever is later of today and
              the current end date, so renewing early keeps what is left and
              renewing late does not bill for the gap - and it un-pauses, since
              somebody pressing 更新 means the clinic is working again.
            */}
            <Select
              id="renew-months"
              value={renewMonths}
              onChange={setRenewMonths}
              options={[
                { value: '1', label: '1か月' },
                { value: '3', label: '3か月' },
                { value: '6', label: '6か月' },
                { value: '12', label: '12か月' },
              ]}
            />
            <button
              className="btn btn--sm"
              type="button"
              disabled={renewing}
              onClick={() => void renew()}
            >
              {renewing ? '更新中…' : '契約を更新'}
            </button>
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => {
                // Reopening always starts from what is stored, never from an
                // abandoned edit.
                setForm(formFrom(clinic));
                setEditing((open) => !open);
              }}
            >
              {editing ? '編集を閉じる' : '編集'}
            </button>
            <Link className="btn btn--ghost btn--sm" to="/admin/tenants">
              一覧に戻る
            </Link>
          </>
        }
      >
        <EditPanel open={editing} onClose={() => setEditing(false)} onSubmit={saveClinic}>
          <Field label="クリニック名" htmlFor="c-name" required>
            <input
              id="c-name"
              required
              maxLength={200}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field
            label="ステータス"
            htmlFor="c-status"
            hint="稼働中以外にすると、所属ユーザーのログインは直ちに無効になります。"
          >
            <Select
              id="c-status"
              value={form.status}
              onChange={(status) => setForm({ ...form, status })}
              options={[
                { value: 'ACTIVE', label: '稼働中' },
                { value: 'SUSPENDED', label: '利用停止' },
                { value: 'ARCHIVED', label: 'アーカイブ' },
              ]}
            />
          </Field>
          <Field
            label="契約プラン"
            htmlFor="c-plan"
            hint="変更は現在の契約に即時反映されます。上限はシステム設定のプランで編集します。"
          >
            <Select
              id="c-plan"
              value={form.planId}
              onChange={(planId) => setForm({ ...form, planId })}
              options={[
                { value: '', label: '変更しない' },
                /*
                  What each plan allows, on the option itself.
                  A plan with fewer stores than this clinic already has is
                  refused by the API - correctly, the stores exist - and the
                  refusal reaches the screen as a generic conflict. Saying so
                  before it is chosen is cheaper than explaining it afterwards.
                */
                ...plans.items.map((plan) => {
                  const limit = plan.limits.stores;
                  const tooSmall = limit !== undefined && stores.length > limit;
                  return {
                    value: plan.id,
                    label: `${plan.name}（記事 ${limitText(plan.limits.articles_per_month)}/店舗・店舗数 ${limitText(limit)}）${
                      tooSmall ? ' ※現在の店舗数を下回ります' : ''
                    }`,
                  };
                }),
              ]}
            />
          </Field>
          {/*
            The contract, which is not the clinic's own status above. Suspending
            a clinic stops its people signing in; pausing the contract leaves
            them signed in and reading, with no allowance to generate against.
          */}
          <Field
            label="契約状態"
            htmlFor="c-subscription"
            hint="一時停止にすると生成枠がなくなり、記事も画像も作成できなくなります。登録済みの内容と記事はそのまま残ります。解約は契約の終了で、再開すると新しい契約期間が始まります。"
          >
            <Select
              id="c-subscription"
              value={form.subscriptionStatus}
              onChange={(subscriptionStatus) => setForm({ ...form, subscriptionStatus })}
              options={[
                { value: 'ACTIVE', label: '利用中' },
                { value: 'PAUSED', label: '一時停止' },
                { value: 'CANCELED', label: '解約' },
              ]}
            />
          </Field>
          {/*
            The date itself, editable.

            The 契約を更新 button in the header is the everyday action and does
            the arithmetic - so many months from whichever is later. This is for
            what that cannot express: a date written into a contract, a
            correction, or empty for open-ended.
          */}
          <Field
            label="契約終了日"
            htmlFor="c-ends-at"
            hint="空欄は無期限。終了日を過ぎるとAI機能のみ停止します。"
          >
            <DateInput
              id="c-ends-at"
              value={form.endsAt}
              onChange={(endsAt) => setForm({ ...form, endsAt })}
              clearLabel="無期限にする"
            />
          </Field>
          {/*
            The billing anchor. Rarely touched, and destructive when it is -
            said here rather than discovered afterwards.
          */}
          <Field
            label="利用枠のリセット日"
            htmlFor="c-period-end"
            hint="毎月この日に記事・画像の枠が戻ります。変更すると新しい期間が始まり、当月の使用数は0から数え直しになります。"
          >
            <DateInput
              id="c-period-end"
              value={form.periodEnd}
              onChange={(periodEnd) => setForm({ ...form, periodEnd })}
              min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
              clearLabel={null}
            />
          </Field>
          <Field label="サイト名" htmlFor="c-site-name">
            <input
              id="c-site-name"
              maxLength={200}
              value={form.siteName}
              onChange={(e) => setForm({ ...form, siteName: e.target.value })}
            />
          </Field>
          <Field label="サイトURL" htmlFor="c-site-url" hint="https のみ登録できます。">
            <input
              id="c-site-url"
              type="url"
              placeholder="https://"
              value={form.siteUrl}
              onChange={(e) => setForm({ ...form, siteUrl: e.target.value })}
            />
          </Field>
          <Field label="文体・トーン" htmlFor="c-tone" hint="店舗が個別に上書きできます。">
            <input
              id="c-tone"
              maxLength={40}
              value={form.defaultTone}
              onChange={(e) => setForm({ ...form, defaultTone: e.target.value })}
            />
          </Field>
          <Field
            label="対策キーワード（既定）"
            htmlFor="c-keywords"
            span="full"
            hint="全店舗の記事に渡されます。店舗ごとに上書きできます。"
          >
            <KeywordInput
              id="c-keywords"
              value={form.defaultKeywords}
              onChange={(defaultKeywords) => setForm({ ...form, defaultKeywords })}
            />
          </Field>
          <ClinicProfileFields idPrefix="c" value={profile} onChange={setProfile} />
          <Field label="WordPressカテゴリ" htmlFor="c-wp">
            <input
              id="c-wp"
              maxLength={120}
              value={form.wordpressCategory}
              onChange={(e) => setForm({ ...form, wordpressCategory: e.target.value })}
            />
          </Field>
        </EditPanel>

        {pending ? <SkeletonTiles /> : (
        <div className="grid grid--4">
          {/*
            The brand fill, like the dashboard's headline row. These four are
            the figures the page exists to answer - is this clinic working, on
            what, until when - and on the pale tile they read as four more
            fields among thirty.
          */}
          <Stat label="ステータス" value={<StatusPill status={clinic.status} />} small />
          <Stat
            label="契約プラン"
            value={
              clinic.plan_id ? (
                // The name, with the slug beside it: the name is what was sold,
                // the slug is what appears in the ledger and in URLs.
                <Link to="/admin/settings">
                  {clinic.plan_name ?? clinic.plan_id}
                </Link>
              ) : (
                '未設定'
              )
            }
            small
            hint={
              clinic.subscription_status ? (
                <StatusPill status={clinic.subscription_status} />
              ) : (
                <Pill tone="warn">契約なし</Pill>
              )
            }
          />
          {/*
            The contract's end, not the billing month's. The month rolls on its
            own and tells an operator nothing; the term is the date somebody has
            to act on, and the tile says how long is left rather than making
            them subtract.
          */}
          <Stat
            label="契約期間"
            value={clinic.ends_at ? jaDate(clinic.ends_at) : '無期限'}
            small
            hint={
              termDays === null ? (
                <span className="muted small">終了日は設定されていません</span>
              ) : termDays < 0 ? (
                <Pill tone="danger">終了（AI機能停止中）</Pill>
              ) : termDays <= 30 ? (
                <Pill tone="warn">残り{jaNumber(termDays)}日</Pill>
              ) : (
                <span className="muted small">残り{jaNumber(termDays)}日</span>
              )
            }
          />
          {/*
            The billing month's boundary, which is NOT the contract's end - the
            tile beside it is that. This is the date the monthly allowance
            resets: articles, images and AI spend all go back to zero and the
            next period's counting starts. It rolls forward on its own.
          */}
          <Stat
            label="利用枠のリセット日"
            value={jaDate(clinic.current_period_end)}
            small
            hint={<span className="muted small">毎月この日に記事・画像の枠が戻ります</span>}
          />
        </div>
        )}

        {/*
          Everything registered about the clinic, on the clinic's own page.

          It used to be three of these - site name, URL, timezone - and the rest
          of what a clinic actually is (its operating entity, its address, the
          person accountable for its advertising) was visible only by opening
          the edit panel. That is backwards: those are the values an article is
          allowed to state, so they are what an operator comes here to check.
          Timezone is gone with the field that set it; every clinic is
          Asia/Tokyo and the column default says so.
        */}
        <dl className="factgrid">
          <Fact label="サイトURL">
            {clinic.site_url ? (
              // Untrusted outbound link: no referrer, no window.opener.
              <a href={clinic.site_url} target="_blank" rel="noopener noreferrer">
                {clinic.site_url}
              </a>
            ) : null}
          </Fact>
          <Fact label="サイト名">{clinic.site_name}</Fact>
          <Fact label="法人名">{clinic.corporate_name}</Fact>
          <Fact label="代表者">{clinic.representative}</Fact>
          <Fact label="広告管理責任者">{clinic.ad_manager_name}</Fact>
          <Fact label="所在地">
            {[clinic.prefecture, clinic.city, clinic.address].filter(Boolean).join(' ') || null}
          </Fact>
          <Fact label="電話番号">{clinic.phone}</Fact>
          <Fact label="標準トーン">{clinic.default_tone}</Fact>
          <Fact label="WordPressカテゴリ">{clinic.wordpress_category}</Fact>
          <Fact label="対策キーワード（既定）">
            {clinic.default_keywords.length > 0 ? clinic.default_keywords.join('・') : null}
          </Fact>
          <Fact label="登録日">{jaDate(clinic.created_at)}</Fact>
        </dl>
      </Card>

      {/* The clinic's theme. The JMC workspace itself keeps the default. */}
      <ThemePicker endpoint={`/admin/clinics/${clinic.id}/theme`} value={clinic.brand_color ?? null} />

      <div className="grid grid--2">
        <Card
          title={`店舗（${stores.length}）`}
          actions={
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => setAddingStore((open) => !open)}
            >
              {addingStore ? '閉じる' : '＋ 店舗を追加'}
            </button>
          }
        >
          {/*
            A dialog, not a panel that pushes the list down.

            Adding a store is a discrete act with its own fields, and the panel
            version was a single text box wearing the page's whole width. The
            details are the ones registration takes, so a store added later is
            not a poorer record than one added at the start.
          */}
          <ModalForm
            open={addingStore}
            onClose={() => {
              setAddingStore(false);
              setNewStore(emptyStore());
            }}
            title="店舗を追加"
            wide
            caption={
              storeLimit === null
                ? undefined
                : `契約プランの上限は${jaNumber(storeLimit)}店舗です（現在${jaNumber(stores.length)}店舗）。`
            }
            onSubmit={async () => {
              await post(`/admin/clinics/${clinic.id}/stores`, {
                name: newStore.name,
                ...storeProfilePayload(newStore),
                ...(newStore.wordpressCategory
                  ? { wordpressCategory: newStore.wordpressCategory }
                  : {}),
              });
              setNewStore(emptyStore());
            }}
            submitLabel="登録する"
          >
            {/*
              Said before the button, not after the refusal. The old hint told
              an operator what to do about a limit they had no way of knowing
              they were at; this one says they are at it.
            */}
            {atStoreLimit && (
              <p className="alert field--full" role="status">
                契約プランの店舗数上限（{jaNumber(storeLimit ?? 0)}）に達しています。
                追加するには先にプランを変更してください。
              </p>
            )}
            <Field label="店舗名" htmlFor="s-new-name" required span="full">
                <input
                  id="s-new-name"
                  required
                  maxLength={200}
                  value={newStore.name}
                  placeholder="例：渋谷院"
                  onChange={(e) => setNewStore({ ...newStore, name: e.target.value })}
                />
              </Field>
              <StoreProfileFields
                idPrefix="s-new"
                value={newStore}
                // The component owns the address fields only; the name and the
                // category live beside them and must survive the merge.
                onChange={(next) => setNewStore((current) => ({ ...current, ...next }))}
              />
              <Field
                label="WordPressカテゴリ"
                htmlFor="s-new-wp"
                hint="空欄ならクリニックの設定を使います。"
              >
                <input
                  id="s-new-wp"
                  maxLength={120}
                  value={newStore.wordpressCategory}
                  onChange={(e) => setNewStore({ ...newStore, wordpressCategory: e.target.value })}
                />
              </Field>
          </ModalForm>

          {stores.length === 0 ? (
            <Empty>店舗が登録されていません。</Empty>
          ) : (
            <Table
              head={
                <>
                  <th scope="col">店舗名</th>
                  <th scope="col" className="num">
                    担当者
                  </th>
                  <th scope="col">状態</th>
                  <th scope="col">登録日</th>
                </>
              }
            >
              {stores.map((store) => (
                <tr key={store.id} id={`store-${store.id}`} className="row-link">
                  <td>
                    <RowLink to={`/admin/stores/${store.id}`}>{store.name}</RowLink>
                  </td>
                  <td className="num">{jaNumber(store.user_count)}</td>
                  <td>
                    <StatusPill status={store.status} />
                  </td>
                  <td>{jaDate(store.created_at)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card
          title={`ユーザー（${members.length}）`}
          actions={
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => setAddingMember((open) => !open)}
            >
              {addingMember ? '閉じる' : '＋ ユーザーを追加'}
            </button>
          }
        >
          <AddMember
            clinicId={clinic.id}
            open={addingMember}
            onClose={() => setAddingMember(false)}
            stores={stores.map((store) => ({ id: store.id, name: store.name }))}
          />

          {members.length === 0 ? (
            <Empty>所属ユーザーがいません。</Empty>
          ) : (
            <Table
              head={
                <>
                  <th scope="col">氏名</th>
                  <th scope="col">権限・担当店舗</th>
                  <th scope="col">最終ログイン</th>
                  <th scope="col" />
                </>
              }
            >
              {members.map((member) => (
                <tr key={member.id} className="row-link">
                  <td>
                    {/*
                      The name goes to the person. Every other name on this
                      screen links to the thing it names, and this one was the
                      exception - an operator wanting to suspend an account or
                      check its last sign-in had to go and search the user list.
                    */}
                    <RowLink to={`/admin/users/${member.id}`}>
                      {member.display_name || member.email || '—'}
                    </RowLink>
                    <span className="muted small" style={{ display: 'block' }}>
                      {member.email}
                    </span>
                  </td>
                  <td>
                    {member.clinic_admin ? (
                      <Pill>{ROLE_LABEL.CLINIC_ADMIN}（全店舗）</Pill>
                    ) : member.stores.length === 0 ? (
                      <Pill tone="warn">担当店舗なし</Pill>
                    ) : (
                      <span className="dashcounts">
                        {member.role === 'STORE_ADMIN' && <Pill>{ROLE_LABEL.STORE_ADMIN}</Pill>}
                        {member.stores.map((store) => (
                          <Pill key={store.id} tone="muted">
                            {store.name}
                          </Pill>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>{jaDate(member.last_login_at)}</td>
                  <td>
                    <MemberEditor
                      clinicId={clinic.id}
                      member={member}
                      stores={stores.map((store) => ({ id: store.id, name: store.name }))}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>

      <Card
        title="今期の上限と実績"
        caption="上限はプランの値です。「台帳」は実際に予約・消費された数で、記事の作成を止めるのはこの数字です。"
      >
        <div className="grid grid--4" style={{ marginBottom: 14 }}>
          <Stat
            label="記事（店舗ごと・月）"
            value={limitText(clinic.plan_limits.articles_per_month)}
            small
          />
          <Stat
            label="記事（クリニック合計・月）"
            value={limitText(clinic.plan_limits.clinic_articles_per_month)}
            small
          />
          <Stat label="店舗数の上限" value={limitText(clinic.plan_limits.stores)} small />
          <Stat
            label="対象期間"
            value={`${jaDate(clinic.current_period_start)}〜${jaDate(clinic.current_period_end)}`}
            small
          />
        </div>

        {reconciliation.length === 0 ? (
          <Empty>契約期間がないため、今期の集計はありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">店舗</th>
                <th scope="col" className="num">
                  台帳（今期）
                </th>
                <th scope="col" className="num">
                  実データ（今期）
                </th>
                <th scope="col" className="num">
                  記事総数
                </th>
                <th scope="col">照合</th>
              </>
            }
          >
            {reconciliation.map((row) => {
              const gap = row.actual - row.ledger;
              return (
                <tr key={row.store_id}>
                  <td>{row.store_name}</td>
                  <td className="num">{jaNumber(row.ledger)}</td>
                  <td className="num">{jaNumber(row.actual)}</td>
                  <td className="num">{jaNumber(row.total)}</td>
                  <td>
                    {gap === 0 ? (
                      <Pill tone="ok">一致</Pill>
                    ) : (
                      /*
                       * A difference is not necessarily a fault, which is why it
                       * is reported rather than corrected: an article created
                       * before the create path charged quota counts once in the
                       * table and never in the ledger, and a deleted article is
                       * the same story backwards. The clinic is shown only the
                       * ledger - it is what refuses the next article - and this
                       * row is where JMC can see the two apart.
                       */
                      <Pill tone="warn">{gap > 0 ? `台帳が ${gap} 件少ない` : `台帳が ${-gap} 件多い`}</Pill>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
        <p className="muted small">
          記事の件数のみを集計しています。本文はJMCからは参照できません（閲覧にはサポート権限の付与が必要です）。
        </p>
      </Card>

      <Card title="利用実績">
        <div className="grid grid--4" style={{ marginBottom: 14 }}>
          <Stat label="記事作成（合計）" value={jaNumber(totals.articles)} />
          <Stat label="画像生成（合計）" value={jaNumber(totals.images)} />
          <Stat
            label="AI費用（合計）"
            value={`$${(totals.cost / 1_000_000).toFixed(2)}`}
            small
            hint={<span className="muted small">マイクロUSD建てで記録</span>}
          />
          <Stat label="記録期間" value={`${usage.length} ヶ月`} small />
        </div>

        {usage.length === 0 ? (
          <Empty>利用実績がまだありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">期間</th>
                <th scope="col" className="num">
                  記事
                </th>
                <th scope="col" className="num">
                  画像
                </th>
                <th scope="col" className="num">
                  AI費用（USD）
                </th>
              </>
            }
          >
            {usage.map((row) => (
              <tr key={row.period_start}>
                <td>{monthLabel(row.period_start)}</td>
                <td className="num">{jaNumber(row.articles_used)}</td>
                <td className="num">{jaNumber(row.images_used)}</td>
                <td className="num">${(Number(row.ai_micro_cost_used) / 1_000_000).toFixed(2)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/*
        Which websites the clinic's articles may be built FROM - above the panel
        that carries 「リンクから追加」, because it is the rule that button obeys.
      */}
      <ClinicAllowedHosts
        clinicId={clinic.id}
        hosts={allowedHosts.items}
        onChanged={() => void revalidator.revalidate()}
      />

      {/*
        What the clinic's articles may state.
        First among the support panels, because a clinic that cannot generate is
        almost always a persona or a treatment left in draft - and until now an
        operator could see the count and not the row.
      */}
      <ClinicFacts
        clinicId={clinic.id}
        data={facts}
        stores={stores.map((store) => ({ id: store.id, name: store.name }))}
      />

      {/*
        Written by JMC, requested by the clinic. On the clinic's own screen
        because that is where an operator already is when a request arrives.
      */}
      <ClinicPromptEditor
        clinicId={clinic.id}
        data={promptStyles}
        onChanged={() => void revalidator.revalidate()}
      />

      <Card
        title="このクリニックの監査ログ"
        caption="権限付与・停止・設定変更・拒否されたアクセスの記録です。記事本文は記録されません。"
        actions={
          <Link className="btn btn--ghost btn--sm" to={`/admin/audit?clinicId=${clinic.id}`}>
            すべて見る
          </Link>
        }
      >
        {activity.length === 0 ? (
          <Empty>記録された操作はまだありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">操作</th>
                <th scope="col">結果</th>
                <th scope="col">日時</th>
              </>
            }
          >
            {activity.slice(0, 8).map((entry) => (
              <tr key={entry.id}>
                <td>{entry.action}</td>
                <td>
                  <Pill tone={entry.outcome === 'DENIED' ? 'danger' : 'ok'}>{entry.outcome}</Pill>
                </td>
                <td>{new Date(entry.created_at).toLocaleString('ja-JP')}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/*
        Offboarding, and the only place it can happen. Kept at the bottom of the
        page and behind a typed confirmation because it takes the clinic's stores
        with it - `stores.clinic_id` is the one RESTRICT here, so the server
        deletes them first, in one transaction.
      */}
      <Card title="クリニックの削除" caption="取り消せません。関連するすべてのデータが消えます。">
        <DangerButton
          label="このクリニックを削除する"
          confirmText={clinic.name}
          consequences={[
            `店舗 ${stores.length} 件とその会員`,
            '記事・下書き・節・画像のすべて',
            '登録済みの施術・強み・監修医・CTA・ペルソナ',
            'ウェブサイトの取得元と取得履歴',
            '契約・利用量の記録',
          ]}
          onConfirm={() => del(`/admin/clinics/${clinic.id}`)}
          onDone={() => void navigate('/admin/tenants')}
        />
        <p className="muted small" style={{ marginBottom: 0 }}>
          監査ログは残ります。誰が何をしたかの記録は、対象が消えても失われません。
        </p>
      </Card>
    </>
  );
}
